// PromptTextEditor.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Box,
    Grid,
    Paper,
    TextField,
    Typography,
    Chip,
    List,
    ListItem,
    ListItemText,
    ListSubheader,
    Divider,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Fab,
    CircularProgress,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import DoneIcon from "@material-ui/icons/Done";
import WarningIcon from "@material-ui/icons/Warning";
import { Close, ContactsOutlined, Done, Edit, ErrorOutlineRounded } from "@material-ui/icons";
import { enhancePromptAPI, deployPromptAPI, editPromptAPI } from "../../APIs/PromptHubUtility";
import SaveIcon from "@material-ui/icons/Save";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import Button from "components/CustomButtons/Button.js";

const useStyles = makeStyles((theme) => ({
    shell: {
        borderRadius: 14,
    },
    btnRow: {
        display: "flex",
        gap: theme.spacing(1.5),
        flexWrap: "wrap",
        justifyContent: "flex-end",
        marginRight: "20px",
        marginBottom: "20px",
    },
    editorWrap: {
        padding: theme.spacing(2),
    },
    sidebar: {
        padding: theme.spacing(2),
        borderLeft: `1px solid ${theme.palette.divider}`,
        minHeight: "100%",
    },
    subheader: {
        background: "transparent",
        paddingLeft: 0,
    },
    chipRow: {
        display: "flex",
        gap: theme.spacing(1),
        flexWrap: "wrap",
        marginTop: theme.spacing(1),
    },
    requiredItem: {
        borderRadius: 10,
        marginBottom: 6,
    },
    reqOk: {
        background: "rgba(76,175,80,0.1)",
    },
    reqMissing: {
        background: "rgba(244,67,54,0.12)",
    },
    reqExtra: {
        background: "rgba(255,152,0,0.14)",
    },
    banner: {
        borderRadius: 10,
        padding: theme.spacing(1),
        marginBottom: theme.spacing(1.5),
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
    },
    bannerError: { background: "rgba(244,67,54,0.12)" },
    bannerWarn: { background: "rgba(255,152,0,0.14)" },
    monospace: {
        fontFamily:
            'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
    },
}));

export default function PromptTextEditor({
    value,
    onChange,
    requiredFields = [],
    application_name,
    latest_version,
    deployed_version,
    selectedVersion,
    promptId,
    loadAll,
    latestVersionObj,
}) {
    const classes = useStyles();
    const [text, setText] = useState(value ?? "");
    const textareaRef = useRef();

    const [saving, setSaving] = useState(false);
    const [enhancing, setEnhancing] = useState(false);
    const [deploying, setDeploying] = useState(false);
    const [openCommitDialog, setOpenCommitDialog] = useState(false);
    const [commitMessage, setCommitMessage] = useState("");

    const [openEnhanceDialog, setOpenEnhanceDialog] = useState(false);
    const [openDeployDialog, setOpenDeployDialog] = useState(false);

    const handleEnhance = async () => {
        try {
            setEnhancing(true);
            const res = await enhancePromptAPI(text);
            setText(res);
            if (res) onChange(res);
        } finally {
            setEnhancing(false);
            setOpenEnhanceDialog(false);

        }
    };

    const handleDeploy = async () => {
        const versionToDeploy = selectedVersion?.version ?? latestVersionObj?.version;
        if (!versionToDeploy) return;
        alert(
            `This prompt (v${versionToDeploy}) will be deployed and used for all further purposes.`
        );
        try {
            setDeploying(true);
            await deployPromptAPI(promptId, versionToDeploy);
            await loadAll();
        } finally {
            setDeploying(false);
        }
    };

    const handleSaveNewVersion = async () => {
        if (!commitMessage.trim()) {
            alert("Please enter a commit message before saving a new version.");
            return;
        }
        try {
            setSaving(true);
            const formData = new FormData();
            formData.append("prompt_id", promptId);
            formData.append("prompt", text);
            formData.append("commit_message", commitMessage);
            await editPromptAPI(formData);
            setCommitMessage("");
            setOpenCommitDialog(false);
            await loadAll();
        } finally {
            setSaving(false);
        }
    };

    function extractFields(promptText) {
        const found = new Map();
        const order = [];
        const re = /\{([A-Za-z0-9_]+)\}/g;
        let m;

        while ((m = re.exec(promptText)) !== null) {
            const start = m.index;           // index of '{'
            const end = start + m[0].length; // index right after '}'

            // Skip if this {name} is part of a double-brace block like {{name}}.
            // Guard: char before '{' must NOT be '{' and char after '}' must NOT be '}'.
            const prevChar = promptText[start - 1];
            const nextChar = promptText[end];

            if (prevChar === '{' || nextChar === '}') continue;

            const name = `{${m[1]}}`;

            if (!found.has(name)) found.set(name, { name, indices: [] });
            found.get(name).indices.push(start);
            order.push({ name, index: start });
        }

        return { found, order };
    }

    useEffect(() => {
        setText(value ?? "");
    }, [value]);

    const analysis = useMemo(() => {
        const { found, order } = extractFields(text);

        // which required fields are present/missing
        const present = [];
        const missing = [];

        for (const rf of requiredFields) {
            if (found.has(rf)) present.push(rf);
            else missing.push(rf);
        }

        // extras = any field found that isn't required
        const extras = [];
        for (const [name, meta] of found.entries()) {
            if (!requiredFields.includes(name)) {
                for (const pos of meta.indices) {
                    extras.push({ name, index: pos });
                }
            }
        }
        // sort extras by position asc
        extras.sort((a, b) => a.index - b.index);

        return { present, missing, extras, order };
    }, [text, requiredFields]);

    const setCursorAt = (pos) => {
        const el = textareaRef.current;
        if (!el) return;
        el.focus();
        el.setSelectionRange(pos, pos);
    };

    const handleChange = (e) => {
        const next = e.target.value;
        setText(next);
        onChange(next);
    };

    const [missingCount, setMissingCount] = useState(0);
    const [extraCount, setExtraCount] = useState(0);

    useEffect(() => {
        setMissingCount(analysis.missing.length);
        setExtraCount(analysis.extras.length);
    }, [analysis]);

    const [isSaveButtonDisabled, setIsSaveButtonDisabled] = useState(true);
    const [isEditEnabled, setIsEditEnabled] = useState(false);

    useEffect(() => {
        setIsSaveButtonDisabled((!text.trim() || requiredFields.length !== analysis.present.length || analysis.extras.length > 0 || !isEditEnabled));
    }, [text, requiredFields, analysis, isEditEnabled]);


    return (
        <Paper elevation={0} className={classes.shell}>
            {/* confirm enhance dialog */}
            <Dialog
                open={openEnhanceDialog}
                onClose={() => setOpenEnhanceDialog(false)}
                aria-labelledby="enhance-dialog-title"
                aria-describedby="enhance-dialog-description"
            >
                <DialogTitle id="enhance-dialog-title">Enhance Prompt</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" id="enhance-dialog-description">
                        Do you want to enhance this prompt using metaprompting?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenEnhanceDialog(false)} color="secondary">
                        Cancel
                    </Button>
                    <Button onClick={handleEnhance} color="quCreateColor">
                        {enhancing ? <><CircularProgress color="white" size={20} style={{ marginRight: "10px" }} /> Enhancing...</> : "Enhance Prompt"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Save new version dialog */}
            <Dialog
                open={openCommitDialog}
                onClose={() => setOpenCommitDialog(false)}
                aria-labelledby="commit-dialog-title"
                aria-describedby="commit-dialog-description"
            >
                <DialogTitle id="commit-dialog-title">Save New Version</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" id="commit-dialog-description">
                        Please enter a commit message before saving a new version.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenCommitDialog(false)} color="secondary">
                        Cancel
                    </Button>
                    <Button onClick={handleSaveNewVersion} color="quCreateColor">
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Deploy dialog */}
            <Dialog
                open={openDeployDialog}
                onClose={() => setOpenDeployDialog(false)}
                aria-labelledby="deploy-dialog-title"
                aria-describedby="deploy-dialog-description"
            >
                <DialogTitle id="deploy-dialog-title">Deploy Prompt</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" id="deploy-dialog-description">
                        Are you sure you want to deploy this prompt?
                        This will replace the current deployed version.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDeployDialog(false)} color="secondary">
                        Cancel
                    </Button>
                    <Button onClick={handleDeploy} color="quCreateColor">
                        Deploy
                    </Button>
                </DialogActions>
            </Dialog>

            <Grid container>
                {/* Editor */}
                <Grid item xs={12} md={8}>
                    <Box className={classes.editorWrap}>
                        {/* Status banners */}
                        {missingCount > 0 && (
                            <Box className={`${classes.banner} ${classes.bannerError}`}>
                                <ErrorOutlineIcon fontSize="small" />
                                <Typography variant="body2" style={{ marginLeft: "8px" }}>
                                    Missing required fields:{" "}
                                    <strong>{missingCount}</strong>
                                </Typography>
                            </Box>
                        )}
                        {extraCount > 0 && (
                            <Box className={`${classes.banner} ${classes.bannerWarn}`}>
                                <WarningIcon fontSize="small" />
                                <Typography variant="body2" style={{ marginLeft: "8px" }}>
                                    Extra fields present: <strong>{extraCount}</strong>
                                    {" — click an item on the right to jump to its position."}
                                </Typography>
                            </Box>
                        )}


                        <Box style={{ position: 'relative' }}>
                            <TextField
                                variant="outlined"
                                fullWidth
                                multiline
                                inputRef={textareaRef}
                                value={text}
                                onChange={handleChange}
                                placeholder="Write your prompt here… Use {FIELD_NAME} for variables."
                                rows={20}
                                inputProps={{ className: classes.monospace }}
                                disabled={!isEditEnabled || enhancing}
                            />
                            <Fab
                                size="small"
                                color="primary"
                                onClick={() => setIsEditEnabled(!isEditEnabled)}
                                style={{
                                    position: 'absolute',
                                    bottom: 8,
                                    right: 8,
                                }}
                            >
                                {isEditEnabled ? <Close /> : <Edit />}
                            </Fab>
                        </Box>

                        <Box mt={1.5} className={classes.chipRow}>
                            <Box>
                                {
                                    isEditEnabled && <>
                                        <Chip
                                            size="small"
                                            icon={<DoneIcon style={{ fontSize: 16, color: requiredFields.length === analysis.present.length ? "white" : "black" }} />}
                                            label={`Required: ${requiredFields.length}`}
                                            style={{
                                                backgroundColor: requiredFields.length === analysis.present.length ? "darkgreen" : "grey",
                                                color: "white",
                                            }}
                                        />
                                        <Chip
                                            size="small"
                                            style={{
                                                marginRight: "8px",
                                                marginLeft: "8px",
                                                backgroundColor: missingCount > 0 ? "darkred" : "grey",
                                                color: "white",
                                            }}
                                            label={`Missing: ${missingCount}`}
                                        />
                                        <Chip
                                            size="small"
                                            style={{
                                                backgroundColor: extraCount > 0 ? "darkorange" : "grey",
                                                color: "white",
                                            }}
                                            label={`Extras: ${extraCount}`}
                                        />
                                    </>
                                }
                            </Box>
                        </Box>
                    </Box>
                </Grid>

                {/* Required fields sidebar */}
                <Grid item xs={12} md={4}>
                    <Box className={classes.sidebar}>
                        <Box display="flex" justifyContent="flex-end" flexWrap="wrap" marginTop={"auto"} gridGap={8}>
                            {selectedVersion && (
                                <Chip
                                    variant="outlined"
                                    label={`Loaded v${selectedVersion}`}
                                />
                            )}
                            <Chip label={`Latest v${latest_version}`} />
                            <Chip style={{ backgroundColor: "#20677b", color: "white" }} label={`Deployed v${deployed_version}`} />

                        </Box>
                        <List
                            dense
                            subheader={
                                <ListSubheader className={classes.subheader} disableSticky>
                                    Required fields
                                </ListSubheader>
                            }
                        >
                            {requiredFields.map((rf) => {
                                const isMissing = analysis.missing.includes(rf);
                                const isPresent = analysis.present?.includes(rf);
                                return (
                                    <ListItem
                                        key={rf}
                                        className={`${classes.requiredItem} ${isMissing ? classes.reqMissing : classes.reqOk
                                            }`}
                                    >
                                        {isMissing ? (<ErrorOutlineRounded fontSize="small" />) : (<Done fontSize="small" />)}
                                        <ListItemText
                                            primary={<span style={{ marginLeft: "4px" }} className={classes.monospace}>{`${rf}`}</span>}
                                        />
                                    </ListItem>
                                );
                            })}
                        </List>

                        <Divider style={{ margin: "12px 0" }} />

                        <List
                            dense
                            subheader={
                                <ListSubheader className={classes.subheader} disableSticky>
                                    Extra fields in prompt
                                </ListSubheader>
                            }
                        >
                            {analysis.extras.length === 0 ? (
                                <ListItem className={classes.reqOk}>
                                    <ListItemText primary="No extras detected" />
                                </ListItem>
                            ) : (
                                analysis.extras.map((ex, i) => (
                                    <ListItem
                                        key={`${ex.name}-${i}-${ex.index}`}
                                        className={`${classes.requiredItem} ${classes.reqExtra}`}
                                    >
                                        <ListItemText
                                            primary={
                                                <span className={classes.monospace}>
                                                    {`${ex.name}`}
                                                </span>
                                            }
                                            secondary={
                                                <span className={classes.monospace}>
                                                    {`@ char ${ex.index}`}
                                                </span>
                                            }
                                        />
                                        <Tooltip title="Jump to this position">
                                            <Button
                                                size="small"
                                                onClick={() => setCursorAt(ex.index)}
                                                link
                                                style={{ color: "grey" }}
                                            >
                                                Go to position
                                            </Button>
                                        </Tooltip>
                                    </ListItem>
                                ))
                            )}
                        </List>


                    </Box>
                </Grid>
            </Grid>
            <Divider />

            <Box mt={2} mb={2} pb={2} className={classes.btnRow}>
                <Tooltip title="Improve clarity, structure, and placeholders using metaprompting">
                    <span>
                        <Button
                            variant="outlined"
                            startIcon={"˙⟡"}
                            onClick={() => {
                                setOpenEnhanceDialog(true);
                            }}
                            style={{
                                background: "linear-gradient(45deg,rgb(60, 147, 172),#15384e)"
                            }}
                            disabled={enhancing || !isEditEnabled}
                        >
                            {enhancing ? "Enhancing…" : "Enhance prompt"}
                        </Button>
                    </span>
                </Tooltip>

                <Dialog open={openCommitDialog} fullWidth onClose={() => setOpenCommitDialog(false)}>
                    <DialogTitle>Save new version</DialogTitle>
                    <DialogContent>
                        <TextField
                            label="Commit message"
                            value={commitMessage}
                            onChange={(e) => setCommitMessage(e.target.value)}
                            fullWidth
                            multiline
                            rows={4}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpenCommitDialog(false)}>Cancel</Button>
                        <Button onClick={handleSaveNewVersion} color="quCreateColor" disabled={saving || !commitMessage.trim()}>Save</Button>
                    </DialogActions>
                </Dialog>

                <Tooltip title={isSaveButtonDisabled ? "Missing required fields or extras. Rectify them." : "Save as a new version"}>
                    <span>
                        <Button
                            color={isSaveButtonDisabled ? "disabled" : "success"}
                            variant="contained"
                            startIcon={<SaveIcon />}
                            onClick={() => setOpenCommitDialog(true)}
                            disabled={isSaveButtonDisabled}
                            style={{
                                marginLeft: "16px",
                                marginRight: "16px"
                            }}
                        >
                            {saving ? "Saving…" : "Save new version"}
                        </Button>
                    </span>
                </Tooltip>

                <Tooltip title="Deploy this loaded version to production">
                    <span>
                        <Button
                            color="quCreateColor"
                            variant="contained"
                            startIcon={<CloudUploadIcon />}
                            onClick={() => setOpenDeployDialog(true)}
                            disabled={deploying || !selectedVersion || latestVersionObj.version === deployed_version}
                        >
                            {deploying ? "Deploying…" : "Deploy prompt"}
                        </Button>
                    </span>
                </Tooltip>
            </Box>
        </Paper>
    );
}
